import i18next from 'i18next';
import logger from '@widgets/logger';
import SentryManager from '@widgets/sentry-manager';
import { store } from 'store';
import { history } from '@helpers/history';
import { authActions, accountActions, alertActions } from '@actions';
import { accountConstants } from '@constants/account';
import { ROLE } from '@constants/user';

export const BASE_URL =
    process.env.NODE_ENV !== 'development' ? `${window.location.origin}/` : process.env.REACT_APP_API_URL;

const AuthRoutes = ['/login', '/signup', '/reset-password/', '/forgot-password', '/verify-email/', '/error'];

const ServerErrorCodes = [500, 502, 504];

export const authHeader = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user?.token) {
        return { 'Content-Type': 'application/json', Authorization: 'Bearer ' + user.token };
    } else {
        return {};
    }
};

const errorMiddleware = response => {
    if (response.status === 401) {
        const isAuthUrl = AuthRoutes.some(url => window.location.pathname.startsWith(url));
        if (!isAuthUrl) {
            SentryManager.captureException(response, { context: 'Unauthorized access: 401' });
            store.dispatch(authActions.logout());
        }
    }
    const user = store.getState().auth.user;
    if (response.status === 402 && accountConstants.BILLING_EXPIRED === 0 && user) {
        accountConstants.BILLING_EXPIRED = 1;
        store.dispatch(accountActions.get());
        if (user.role === ROLE.USER) {
            SentryManager.captureException(response, { context: 'Billing expired: 402' });
            store.dispatch(authActions.logout());
        }
    }

    if (ServerErrorCodes.includes(response.status)) {
        SentryManager.captureException(response, { context: `Server error: ${response.status}` });
        history.push('/error');
    }
};

export function handleResponse(response) {
    return response.text().then(text => {
        let data;
        try {
            data = text && JSON.parse(text);
        } catch (e) {
            logger.error('Failed to parse api response:', e);
        }
        if (!response.ok) {
            errorMiddleware(response);
            if (data?.errorCode || data?.ErrorCode) {
                return Promise.reject(data);
            }
            return Promise.reject(data?.message || response.statusText);
        }
        return data;
    });
}

export function handleResponseBlob(response) {
    return response.blob().then(blobObject => {
        const blob = window.URL.createObjectURL(blobObject);
        if (!response.ok) {
            errorMiddleware(response);
            return Promise.reject("Couldn't download image!");
        }
        return blob;
    });
}

/**
 * @param {object} error - BE error object
 * @param {string} specificKey - keys like editFail, deleteFail, etc.
 * @param {object} i18nParams - error fields from BE
 * @param {boolean} withSentry - send error to Sentry
 */
export const handleApiError = (error, specificKey, i18nParams = {}, sentryContext) => {
    if (i18next.exists(`apiErrors.${error?.errorCode}`)) {
        store.dispatch(alertActions.error(i18next.t(`apiErrors.${error?.errorCode}`, i18nParams)));
    } else if (specificKey) {
        store.dispatch(alertActions.error(i18next.t(specificKey, i18nParams)));
    } else {
        store.dispatch(alertActions.error(i18next.t('apiErrors.something_wrong')));
    }
    if (sentryContext && process.env.REACT_APP_STAGE === 'prod') {
        SentryManager.captureException(error, { context: sentryContext });
    }
};

export class RequestQueue {
    constructor() {
        this.currentRequest = null;
    }

    async execute(requestFn) {
        if (this.currentRequest) {
            this.currentRequest.abort();
        }

        const controller = new AbortController();
        this.currentRequest = controller;

        try {
            const currentController = controller;
            const result = await requestFn(controller.signal);
            // Only return the result if this is still the current request
            if (this.currentRequest === currentController) {
                this.currentRequest = null;
                return result;
            }

            return Promise.reject({ aborted: true });
        } catch (error) {
            if (error.name === 'AbortError') {
                return Promise.reject({ aborted: true });
            }
            throw error;
        }
    }
}
